import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, Paper, Container, Alert, TextField, Stack, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import LoginIcon from '@mui/icons-material/Login';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import { useAuth } from './AuthProvider';

// Styled components with BayRockLabs design aesthetic
const LoginContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
  padding: theme.spacing(2),
}));

// Top brand header
const TopHeader = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: 56,
  backgroundColor: '#101010',
  display: 'flex',
  alignItems: 'center',
  padding: '0 16px',
  zIndex: 10,
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.spacing(2),
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
  maxWidth: 480,
  width: '100%',
  textAlign: 'center',
  background: '#ffffff',
  border: '1px solid rgba(226, 232, 240, 0.8)',
}));

const LogoSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const CompanyLogo = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #1e40af 0%, #0891b2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  boxShadow: '0 8px 24px rgba(30, 64, 175, 0.3)',
}));

const LoginButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1e40af 0%, #0891b2 100%)',
  color: 'white',
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(1),
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #0e7490 100%)',
    boxShadow: '0 6px 16px rgba(30, 64, 175, 0.4)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    background: '#9ca3af',
    boxShadow: 'none',
    transform: 'none',
  },
}));

const FeatureList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  textAlign: 'left',
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  color: '#64748b',
  fontSize: '0.95rem',
}));

const LoginPage: React.FC = () => {
  const { login, isLoading, error, dbRequestCode, dbVerifyAndLogin } = useAuth();
  const AUTH_PROVIDER = (process.env.REACT_APP_AUTH_PROVIDER || 'AZURE_AD').toUpperCase();
  const signInButtonLabel = process.env.REACT_APP_SIGNIN_BUTTON_LABEL || 'Sign in with your company email id';
  const signInHelperText = process.env.REACT_APP_SIGNIN_HELP_TEXT || 'Sign in with your corporate Microsoft account to access your expense management dashboard.';
  const resendCooldownDefault = Number(process.env.REACT_APP_DB_RESEND_COOLDOWN || 60);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [cooldown, setCooldown] = useState<number>(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const [requestingCode, setRequestingCode] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const isEmailValid = useMemo(() => /[^@\s]+@[^@\s]+\.[^@\s]+/.test(email), [email]);
  const isCodeValid = useMemo(() => /^\d{6}$/.test(code), [code]);

  const onRequestCode = async () => {
    setLocalError(null);
    setInfoMessage(null);
    if (!isEmailValid) {
      setLocalError('Enter a valid email address');
      return;
    }
    try {
      setRequestingCode(true);
      const resp = await dbRequestCode?.(email);
      setStep('code');
      setCooldown(resendCooldownDefault);
      setInfoMessage(resp?.message || `A 6-digit code has been sent to ${email}.`);
    } catch (e: any) {
      setLocalError(e?.message || 'Failed to request code. Please try again.');
    }
    finally {
      setRequestingCode(false);
    }
  };

  const onVerify = async () => {
    setLocalError(null);
    setInfoMessage(null);
    if (!isCodeValid) {
      setLocalError('Enter the 6-digit code sent to your email');
      return;
    }
    try {
      setVerifying(true);
      await dbVerifyAndLogin?.(email, code);
    } catch (e: any) {
      setLocalError(e?.message || 'Invalid or expired code. Please retry.');
    }
    finally {
      setVerifying(false);
    }
  };

  const handleLogin = async () => {
    try {
      await login();
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  return (
    <LoginContainer maxWidth={false}>
      {/* Brand header */}
      <TopHeader>
        <img
          src="https://cdn.prod.website-files.com/65816ecb0cdfe876cc89a2bb/65f050af19c4be7d8778b05b_BayRock_Labs_White-08.svg"
          alt="BayRock Labs"
          style={{ height: 28 }}
        />
      </TopHeader>

      <LoginPaper elevation={0} sx={{ mt: 8 }}>
        <LogoSection>
          <CompanyLogo>
            <BusinessIcon sx={{ fontSize: 40, color: 'white' }} />
          </CompanyLogo>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              marginBottom: 1,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
          >
            ExpenseManager
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: '#64748b',
              fontWeight: 500,
            }}
          >
            Enterprise Expense Management
          </Typography>
        </LogoSection>

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              mb: 1,
            }}
          >
            Built with Vibe Coding
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: '#64748b', lineHeight: 1.6 }}
          >
            This SaaS was conceived, designed, and developed end‑to‑end using AI‑assisted "vibe coding" workflows. Rapid iteration, clean architecture, and enterprise‑grade patterns—all powered by collaborative AI development.
          </Typography>
        </Box>

        {(error || localError) && (
          <Alert 
            severity="error" 
            sx={{ 
              marginBottom: 3,
              borderRadius: 1,
              '& .MuiAlert-message': {
                fontSize: '0.95rem',
              },
            }}
          >
            {error || localError}
          </Alert>
        )}
        {infoMessage && (
          <Alert 
            severity="info"
            sx={{
              marginBottom: 3,
              borderRadius: 1,
              '& .MuiAlert-message': { fontSize: '0.95rem' },
            }}
          >
            {infoMessage}
          </Alert>
        )}

        {AUTH_PROVIDER === 'DB' ? (
          <Box>
            {step === 'email' && (
              <Stack spacing={2}>
                <TextField
                  label="Work email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  placeholder="you@company.com"
                  disabled={requestingCode}
                />
                <LoginButton
                  variant="contained"
                  size="large"
                  onClick={onRequestCode}
                  disabled={requestingCode || !isEmailValid}
                  startIcon={requestingCode ? undefined : <LoginIcon />}
                  fullWidth
                >
                  {requestingCode ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <CircularProgress size={18} color="inherit" />
                      Sending code...
                    </Box>
                  ) : (
                    'Send login code'
                  )}
                </LoginButton>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  We'll email you a one-time login code if your account exists.
                </Typography>
              </Stack>
            )}

            {step === 'code' && (
              <Stack spacing={2}>
                <TextField
                  label="Verification code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputProps={{ inputMode: 'numeric', pattern: '\\d*', maxLength: 6 }}
                  placeholder="Enter 6-digit code"
                  fullWidth
                  disabled={verifying}
                />
                <LoginButton
                  variant="contained"
                  size="large"
                  onClick={onVerify}
                  disabled={verifying || !isCodeValid}
                  startIcon={verifying ? undefined : <SecurityIcon />}
                  fullWidth
                >
                  {verifying ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <CircularProgress size={18} color="inherit" />
                      Verifying...
                    </Box>
                  ) : (
                    'Verify & Sign in'
                  )}
                </LoginButton>
                <Button
                  variant="text"
                  onClick={onRequestCode}
                  disabled={cooldown > 0}
                >
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                </Button>
                <Button variant="text" onClick={() => setStep('email')}>Use a different email</Button>
              </Stack>
            )}
          </Box>
        ) : (
          <>
          <LoginButton
            variant="contained"
            size="large"
            onClick={handleLogin}
            disabled={isLoading}
            startIcon={<LoginIcon />}
            fullWidth
          >
            {isLoading ? 'Signing in...' : signInButtonLabel}
          </LoginButton>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 1, textAlign: 'center' }}>
            {signInHelperText}
          </Typography>
          </>
        )}

        <FeatureList>
          <Typography
            variant="subtitle2"
            sx={{
              color: '#374151',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            What you can do:
          </Typography>
          
          <FeatureItem>
            <SecurityIcon sx={{ fontSize: 20, marginRight: 2, color: '#0891b2' }} />
            Submit and track expense reports
          </FeatureItem>
          
          <FeatureItem>
            <SecurityIcon sx={{ fontSize: 20, marginRight: 2, color: '#0891b2' }} />
            Upload receipts with automatic data extraction
          </FeatureItem>
          
          <FeatureItem>
            <SecurityIcon sx={{ fontSize: 20, marginRight: 2, color: '#0891b2' }} />
            Manage approvals and budgets
          </FeatureItem>
          
          <FeatureItem>
            <SecurityIcon sx={{ fontSize: 20, marginRight: 2, color: '#0891b2' }} />
            Generate comprehensive reports
          </FeatureItem>
        </FeatureList>

        <Box sx={{ marginTop: 4, paddingTop: 3, borderTop: '1px solid #e2e8f0' }}>
          <Typography
            variant="caption"
            sx={{
              color: '#9ca3af',
              fontSize: '0.85rem',
            }}
          >
            Developed By Bayrock Labs
          </Typography>
        </Box>
      </LoginPaper>
    </LoginContainer>
  );
};

export default LoginPage;
